import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useModalDismiss } from '../ui/useModalDismiss';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Homeowner' },
  { value: 'board', label: 'Board member' },
  { value: 'management', label: 'Management' },
  { value: 'admin', label: 'Administrator' },
];

const ROLE_DESCRIPTIONS = {
  member: 'Standard homeowner access.',
  board: 'Board documents, ACC review, directory, and messaging.',
  management: 'Operational access for property management staff.',
  admin: 'Full portal access, including users and settings.',
};

const RELATIONSHIP_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'co_owner', label: 'Co-owner' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'family_member', label: 'Family member' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'property_manager', label: 'Property manager' },
  { value: 'board_member', label: 'Board member' },
];

const INITIAL_FORM = {
  account_type: 'property',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  role: 'member',
  property_id: '',
  link_type: 'owner',
  is_acc_member: false,
};

const inputClass =
  'w-full px-4 py-3 bg-white border border-amber-100/40 hover:border-secondary/40 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark placeholder-slate-400 text-sm font-medium outline-none transition-all';

const MaterialIcon = ({ name, className = '', size = 20 }) => (
  <span className={`material-symbols-outlined ${className}`} style={{ fontSize: `${size}px` }}>
    {name}
  </span>
);

const CreateUserModal = ({ open, properties, loading, error, onClose, onSubmit }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  useModalDismiss({ isOpen: open, onClose });

  useEffect(() => {
    if (open) setForm(INITIAL_FORM);
  }, [open]);

  if (!open) return null;

  const setAccountType = (accountType) => {
    setForm((current) => ({
      ...current,
      account_type: accountType,
      property_id: accountType === 'property' ? current.property_id : '',
      link_type: accountType === 'property' ? current.link_type || 'owner' : null,
      is_acc_member: accountType === 'property' ? current.is_acc_member : false,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...form,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      property_id: form.account_type === 'property' ? form.property_id : null,
      link_type: form.account_type === 'property' ? form.link_type : null,
    });
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      role="presentation"
      data-modal-backdrop="true"
    >
      <motion.div
        className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8"
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.97, opacity: 0 }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-user-title"
        data-testid="create-user-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-7">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
              <MaterialIcon name="person_add" className="text-secondary" size={24} />
            </div>
            <div>
              <h3 id="create-user-title" className="text-xl font-bold text-primary-dark">
                Add portal user
              </h3>
              <p className="text-sm font-medium text-slate-500 mt-1">
                Create the account and send its secure invitation.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-10 h-10 rounded-full hover:bg-section-bg flex items-center justify-center disabled:opacity-50"
            aria-label="Close add user"
          >
            <MaterialIcon name="close" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Account type
            </p>
            <div
              className="grid grid-cols-2 gap-2 bg-section-bg rounded-2xl p-2"
              role="group"
              aria-label="Account type"
            >
              {[
                { value: 'property', label: 'Property user', icon: 'home' },
                { value: 'staff', label: 'Staff / non-property', icon: 'badge' },
              ].map((option) => {
                const selected = form.account_type === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setAccountType(option.value)}
                    className={`min-h-12 px-4 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all ${selected ? 'bg-white text-primary-dark warm-tactile-shadow' : 'text-slate-500 hover:text-primary-dark'}`}
                    aria-pressed={selected}
                  >
                    <MaterialIcon name={option.icon} size={19} />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="create-user-first-name"
                className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
              >
                First name
              </label>
              <input
                id="create-user-first-name"
                type="text"
                required
                autoComplete="given-name"
                value={form.first_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, first_name: event.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="create-user-last-name"
                className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
              >
                Last name
              </label>
              <input
                id="create-user-last-name"
                type="text"
                autoComplete="family-name"
                value={form.last_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, last_name: event.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="create-user-email"
                className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
              >
                Email
              </label>
              <input
                id="create-user-email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="create-user-phone"
                className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
              >
                Phone
              </label>
              <input
                id="create-user-phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, phone: event.target.value }))
                }
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="create-user-role"
              className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
            >
              Portal role
            </label>
            <select
              id="create-user-role"
              required
              value={form.role}
              onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
              className={inputClass}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs font-medium text-slate-500 mt-2">
              {ROLE_DESCRIPTIONS[form.role]}
            </p>
          </div>

          {form.account_type === 'property' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="create-user-property"
                  className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
                >
                  HOA address
                </label>
                <select
                  id="create-user-property"
                  required
                  value={form.property_id}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, property_id: event.target.value }))
                  }
                  className={inputClass}
                >
                  <option value="">Select an address</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="create-user-relationship"
                  className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
                >
                  Relationship
                </label>
                <select
                  id="create-user-relationship"
                  required
                  value={form.link_type}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, link_type: event.target.value }))
                  }
                  className={inputClass}
                >
                  {RELATIONSHIP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {form.account_type === 'property' && (
            <div className="flex items-center justify-between gap-4 py-2">
              <div>
                <p className="text-sm font-bold text-primary-dark">ACC Committee Member</p>
                <p className="text-xs font-medium text-slate-500 mt-1">
                  Adds ACC review access when the account activates.
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setForm((current) => ({ ...current, is_acc_member: !current.is_acc_member }))
                }
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${form.is_acc_member ? 'bg-secondary' : 'bg-slate-200'}`}
                aria-label="Toggle ACC committee membership"
                aria-pressed={form.is_acc_member}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${form.is_acc_member ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          )}

          {error && (
            <div
              className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="border border-slate-200 text-slate-700 px-6 py-3 rounded-full font-bold text-sm hover:bg-section-bg transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (form.account_type === 'property' && properties.length === 0)}
              className="bg-secondary text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <MaterialIcon name="send" size={18} />
              {loading ? 'Creating...' : 'Create & Send Invite'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CreateUserModal;
